import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';
import { LOCALES, LOCALE_LABELS, type Locale } from '@insolech/i18n';
import { persistPatientLocale } from '@/features/patient-public/patientLocale';

/**
 * Compact, auth-free language switcher for the ANONYMOUS patient pages
 * (reorder-v2.1 hardening item 3). Unlike the authenticated
 * components/layout/LanguageSwitcher, this one never touches useAuth/supabase/
 * profiles (patients have no profile) — it just calls `i18n.changeLanguage`,
 * which persists to localStorage (`caches: ['localStorage']`) and, via the
 * global DirectionEffect, flips `<html dir>` for Arabic. Native <select> for
 * free keyboard/screen-reader support.
 */
export function PatientLanguageSwitcher() {
  const { i18n, t } = useTranslation('common');
  const current = (LOCALES as readonly string[]).includes(i18n.language)
    ? (i18n.language as Locale)
    : 'en';

  const onChange = React.useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const next = e.target.value as Locale;
      persistPatientLocale(next); // explicit choice -> dedicated key (survives reload)
      void i18n.changeLanguage(next);
    },
    [i18n],
  );

  const label = t('language', { defaultValue: 'Language' });

  return (
    <label className="relative inline-flex items-center">
      <span className="sr-only">{label}</span>
      <Globe
        aria-hidden="true"
        className="absolute start-2 top-1/2 -translate-y-1/2 h-4 w-4 text-text-muted pointer-events-none"
      />
      <select
        aria-label={label}
        data-testid="patient-language-switcher"
        value={current}
        onChange={onChange}
        className="h-9 ps-8 pe-7 rounded-md bg-transparent border border-transparent hover:bg-sand-100 text-sm text-text-secondary hover:text-text-primary focus-visible:outline-none focus-visible:border-border-focus appearance-none cursor-pointer"
      >
        {LOCALES.map((loc) => (
          <option key={loc} value={loc}>
            {LOCALE_LABELS[loc]}
          </option>
        ))}
      </select>
    </label>
  );
}
